import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { priceAPI } from '../services/api';

const Pricing = () => {
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await priceAPI.getReactions();
        // Map API data to our format
        const mappedReactions = response.data.map(r => ({
          reaction_type: r.id,
          name: r.name,
          emoji: r.emoji,
          rate: r.price_usd?.toString() || '0.05',
          min: '500',
          max: '10000'
        }));
        setReactions(mappedReactions);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Set default pricing if API fails
        setReactions([
          { reaction_type: 'rocket', name: 'Rocket', emoji: '🚀', rate: '0.05', min: '500', max: '10000' },
          { reaction_type: 'fire', name: 'Fire', emoji: '🔥', rate: '0.05', min: '500', max: '10000' },
          { reaction_type: 'poop', name: 'Poop', emoji: '💩', rate: '0.05', min: '500', max: '10000' },
          { reaction_type: 'redflag', name: 'Red Flag', emoji: '🚩', rate: '0.05', min: '500', max: '10000' },
          { reaction_type: 'favorite', name: 'Star', emoji: '⭐', rate: '0.05', min: '500', max: '10000' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  const getReactionEmoji = (type) => {
    const emojis = {
      'rocket': '🚀',
      'fire': '🔥',
      'poop': '💩',
      'redflag': '🚩',
      'favorite': '⭐'
    };
    return emojis[type?.toLowerCase()] || '✨';
  };

  const getReactionName = (type) => {
    const names = {
      'rocket': 'Rocket',
      'fire': 'Fire',
      'poop': 'Poop',
      'redflag': 'Red Flag',
      'favorite': 'Star'
    };
    return names[type?.toLowerCase()] || type;
  };

  const getReactionDescription = (type) => {
    const descriptions = {
      'rocket': 'Show explosive growth potential - Perfect for launching new tokens',
      'fire': 'Indicate hot and trending tokens - Best for viral momentum',
      'poop': 'Express negative sentiment - Use wisely for bearish signals',
      'redflag': 'Warn about potential risks - Help the community stay safe',
      'favorite': 'Mark as a favorite pick - Highlight quality projects'
    };
    return descriptions[type?.toLowerCase()] || 'Custom reaction for your token';
  };

  const getReactionColor = (type) => {
    const colors = {
      'rocket': 'from-blue-500 to-purple-600',
      'fire': 'from-orange-500 to-red-600',
      'poop': 'from-amber-600 to-yellow-700',
      'redflag': 'from-red-500 to-pink-600',
      'favorite': 'from-yellow-400 to-orange-500'
    };
    return colors[type?.toLowerCase()] || 'from-gray-500 to-gray-600';
  };

  // Calculate popular packages
  const packages = [
    { quantity: 500, label: 'Starter', popular: false },
    { quantity: 1000, label: 'Growth', popular: true },
    { quantity: 2000, label: 'Pro', popular: false },
    { quantity: 5000, label: 'Enterprise', popular: false },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Pricing - Dexreaction | DexScreener Reactions Service</title>
        <meta name="description" content="Affordable DexScreener reactions starting at $0.05 per reaction. Get Rocket 🚀, Fire 🔥, Star ⭐ and more reactions for your token. Minimum order 500 reactions." />
        <meta name="keywords" content="dexscreener reactions, buy dexscreener reactions, dexscreener rocket, dexscreener fire, crypto reactions, token promotion, dexscreener marketing" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Pricing - Dexreaction | DexScreener Reactions" />
        <meta property="og:description" content="Boost your token visibility with DexScreener reactions. Starting at $0.05 per reaction. Fast delivery, secure payment." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dexreaction.com/pricing" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dexreaction Pricing - DexScreener Reactions" />
        <meta name="twitter:description" content="Affordable DexScreener reactions starting at $0.05. Rocket, Fire, Star reactions and more!" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "DexScreener Reactions",
            "description": "Boost your cryptocurrency token visibility with DexScreener reactions",
            "brand": {
              "@type": "Brand",
              "name": "Dexreaction"
            },
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "25.00",
              "highPrice": "500.00",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900">
        {/* Header */}
        <header className="border-b border-purple-500/20">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">🚀</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Dexreaction
              </span>
            </Link>
            <Link
              to="/"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all text-sm"
            >
              Order Now
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                Buy Dexscreener Reactions Rocket and Fire Instantly
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Get 🚀 Rocket, 🔥 Fire, ⭐ Star and more reactions for your token. 
              All reactions start at just <span className="text-purple-400 font-bold">$0.05</span> each.
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl px-6 py-4 border border-purple-500/20">
                <p className="text-3xl font-bold text-purple-400">$0.05</p>
                <p className="text-sm text-gray-400">Per Reaction</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl px-6 py-4 border border-purple-500/20">
                <p className="text-3xl font-bold text-green-400">500</p>
                <p className="text-sm text-gray-400">Minimum Order</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl px-6 py-4 border border-purple-500/20">
                <p className="text-3xl font-bold text-orange-400">10,000</p>
                <p className="text-sm text-gray-400">Maximum Order</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reaction Types Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
              Available Reaction Types
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reactions.map((reaction, index) => (
                <div
                  key={reaction.reaction_type || index}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all hover:transform hover:scale-105"
                >
                  {/* Reaction Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${getReactionColor(reaction.reaction_type)} rounded-2xl flex items-center justify-center mb-4 mx-auto`}>
                    <span className="text-3xl">{reaction.emoji || getReactionEmoji(reaction.reaction_type)}</span>
                  </div>
                  
                  {/* Reaction Name */}
                  <h3 className="text-xl font-bold text-center mb-2">
                    {reaction.name} {reaction.emoji || getReactionEmoji(reaction.reaction_type)}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm text-center mb-4">
                    {getReactionDescription(reaction.reaction_type)}
                  </p>
                  
                  {/* Price */}
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-400">
                      ${parseFloat(reaction.rate || 0.05).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500">per reaction</p>
                  </div>
                  
                  {/* Min/Max */}
                  <div className="mt-4 pt-4 border-t border-slate-700 flex justify-between text-sm">
                    <div>
                      <p className="text-gray-500">Min</p>
                      <p className="font-semibold text-gray-300">{reaction.min || 500}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500">Max</p>
                      <p className="font-semibold text-gray-300">{reaction.max || '10,000'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Package Calculator Section */}
        <section className="py-12 px-4 bg-slate-800/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
              Popular Packages
            </h2>
            <p className="text-gray-400 text-center mb-12">
              Choose a package that fits your needs
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {packages.map((pkg) => {
                const price = (pkg.quantity * 0.05).toFixed(2);
                return (
                  <div
                    key={pkg.quantity}
                    className={`relative bg-slate-800/80 rounded-2xl p-6 border ${
                      pkg.popular 
                        ? 'border-purple-500 shadow-lg shadow-purple-500/20' 
                        : 'border-slate-700'
                    } text-center`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-purple-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                          POPULAR
                        </span>
                      </div>
                    )}
                    <p className="text-sm text-gray-400 mb-2">{pkg.label}</p>
                    <p className="text-2xl font-bold text-white mb-1">
                      {pkg.quantity.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mb-4">reactions</p>
                    <p className="text-xl font-bold text-purple-400">${price}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
              Why Choose Dexreaction?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Fast Delivery</h3>
                <p className="text-gray-400 text-sm">
                  Reactions delivered within minutes after payment confirmation
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Secure Payment</h3>
                <p className="text-gray-400 text-sm">
                  Pay with SOL, ETH, BNB, USDT or USDC via NOWPayments
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💬</span>
                </div>
                <h3 className="font-bold text-lg mb-2">24/7 Support</h3>
                <p className="text-gray-400 text-sm">
                  Our team is always ready to help via live chat or WhatsApp
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Boost Your Token?
            </h2>
            <p className="text-gray-400 mb-8">
              Get started with DexScreener reactions today. No registration required for quick orders!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-xl font-bold text-lg transition-all transform hover:scale-105"
              >
                🚀 Order Now
              </Link>
              <a
                href="https://wa.me/6285174026539"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-600 hover:bg-green-700 rounded-xl font-bold text-lg transition-all"
              >
                💬 Contact Support
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section for SEO */}
        <section className="py-12 px-4 bg-slate-800/30">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              <details className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-semibold hover:bg-slate-700/50">
                  How much do DexScreener reactions cost?
                </summary>
                <p className="px-6 pb-4 text-gray-400">
                  All DexScreener reactions cost $0.05 per reaction. The minimum order is 500 reactions ($25) and the maximum is 10,000 reactions ($500).
                </p>
              </details>
              
              <details className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-semibold hover:bg-slate-700/50">
                  What payment methods do you accept?
                </summary>
                <p className="px-6 pb-4 text-gray-400">
                  We accept cryptocurrency payments including SOL (Solana), ETH (Ethereum), BNB (BNB Smart Chain), USDT, and USDC. Payments are processed securely via NOWPayments.
                </p>
              </details>
              
              <details className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-semibold hover:bg-slate-700/50">
                  How long does delivery take?
                </summary>
                <p className="px-6 pb-4 text-gray-400">
                  Once your payment is confirmed on the blockchain, reactions are typically delivered within minutes. Our team processes orders 24/7.
                </p>
              </details>
              
              <details className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-semibold hover:bg-slate-700/50">
                  Which reactions are available?
                </summary>
                <p className="px-6 pb-4 text-gray-400">
                  We offer all 5 DexScreener reaction types: Rocket 🚀, Fire 🔥, Poop 💩, Red Flag 🚩, and Star ⭐. All reactions are priced the same.
                </p>
              </details>
              
              <details className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-semibold hover:bg-slate-700/50">
                  Is there a discount for bulk orders?
                </summary>
                <p className="px-6 pb-4 text-gray-400">
                  Yes! We offer voucher codes for discounts. Contact our support team for custom pricing on large orders. Registered users may also qualify for special rates.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-slate-800">
          <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm">
            <p>© 2024 Dexreaction. All rights reserved.</p>
            <div className="mt-4 flex justify-center space-x-6">
              <Link to="/" className="hover:text-purple-400">Home</Link>
              <Link to="/pricing" className="hover:text-purple-400">Pricing</Link>
              <a href="https://wa.me/6285174026539" className="hover:text-purple-400">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Pricing;
